import {For,Show,Match,Switch,createSignal, createMemo, ErrorBoundary, createResource } from 'solid-js';
import {CardItem} from 'Shared/frontend/menuPage/CardItem';
import {ImagelessItem} from 'Shared/frontend/menuPage/ImagelessItem';
import {Overlay} from 'Shared/frontend/Overlay';
import {subdoc} from 'Common/ViewUtils';
import {menuUsesImageCards} from 'Shared/model/Menu';
import {jumpToAnchor, SubNav} from 'Shared/frontend/SubNav';
import {Location} from 'Common/Location';
import { SpecialsOverlayContent } from 'Shared/frontend/specialsPosters/SpecialsOverlayContent';
import { SpecialsPoster } from 'Shared/frontend/specialsPosters/SpecialsPoster';
import { css, cx } from '@emotion/css';
import { theme, palette, sizes } from 'Shared/frontend/SettingsProvider';
import { buttonCss, subNavButtonCss } from 'Shared/frontend/styles/buttonStyleCss';
import { dimBackground } from 'Shared/model/settingColour';
import { capitalise } from 'Shared/common/Capitalise';
import { posterGridStyle } from 'Shared/frontend/posterGridStyle';
import { OverlayItem } from 'Shared/frontend/menuPage/OverlayItem';
import { fluidType } from 'Shared/common/FluidType';
import { useServer } from 'Shared/SiteProvider';
import { PageMeta } from 'Shared/frontend/PageMeta';
import { useLocation, useNavigate } from '@solidjs/router';
import { initPageDetails } from 'Shared/frontend/PageDetailsProvider';
import { Reveal } from 'Shared/frontend/Reveal';
import { IgnorePageMarginsStyle } from 'Shared/frontend/IgnorePageMarginsStyle';
import { Poster } from 'Shared/frontend/Poster';
import { StylesProvider, useStylePath, useStyleDetails, initPageStyles } from 'Shared/frontend/widgets/StylesProvider';
import { resolveStyleDetails } from 'Shared/frontend/widgets/Styles';
import { applicableStyles } from 'Shared/frontend/applicableStyles';
import { menuPartCss } from 'Shared/frontend/menuPage/menuPartStyling';
import { AnimationWrap } from 'Shared/frontend/widgets/AnimationWrap';

const stickyStyle = () => css({
	position: 'sticky',
	top: '0',
	zIndex: '10',
});

//XXX it may make sense to separate the grid -- much of MenuPage? -- into separate Standard and Imageless files in time.

const imagelessGridStyle = () => css({
	paddingTop: '2em' //CJ added because every menu needs a bit of space when they have teaser, tagline etc added before items
});

const standardGridStyle = () => css({
	maxWidth: '100%',
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'center',
	gap: '1.5em',
	alignItems: 'stretch',

	['@media screen and (min-width: 745px)']: {
		margin: '0 20px',
	},

	['@media screen and (max-width: 745px)']: {
		margin: '40px auto'
	}
});

const linkWrapStyle = () => css(theme().a,{
	display: 'block',

	['@media screen and (max-width: 745px)']: {
		width: '100%',
	},

	/* The cards look a little wide on narrow desktop pages. Larger margins help a bit. */
//XXX NB only applies to menus with images
	['@media screen and (min-width:600px) and (max-width: 745px)']: {
		marginLeft: fluidType('px',500,745,14,38),
		marginRight: fluidType('px',500,745,14,38)
	}
});

const buttonRowStyle = () => css({
	display: 'flex',
	justifyContent: 'center'
});

//const buttonRowButtonStyle = () => css(theme().widgets.subNav.button, {
const buttonRowButtonStyle = () => css({...theme().button, ...buttonCss()}, {
	textTransform:'capitalize'
});

const subNavButtonStyle = () => css(
	{...theme().subNav.button, ...subNavButtonCss()},
	{
		[`@media (max-width: ${sizes().flipToMobileNav}px)`]: {
			paddingLeft: '1em',  //BEN had 20em but that looked wrong
			paddingRight: '1em', // BEN had 10em but that looked wrong
			marginLeft: '0.25em',
			marginRight: '0.25em',
		}
	}
);

export const specialItemOverlayStyle = () => css({
	display: 'block',
	maxWidth: '450px',
	minHeight: 'auto',
	backgroundColor: dimBackground(palette())
});

const aStyle = () => css(theme().a, {
});

const detailsStyle = () => css(theme().text.plain, {
	textAlign: 'center',
	img: {
		maxWidth:'100%',
		height: 'auto',
	},
});


const pageName = 'frontend/Menu';


export default function MenuPage()
{
	initPageDetails(pageName,'menu');

	const slug = useLocation().pathname.slice(1);

	const [menu] = createResource(() => useServer().read.query({page:pageName,permission:'menu',params:{slug:slug}}));
	const [specials] = createResource(() => useServer().list.query({page:pageName,permission:'specials'}));

	return (
		<Reveal when={menu() && specials()}>
			<MenuPageContents menu={menu()} specials={specials()} />
		</Reveal>
	);
}


//TODO separate grid out into StandardMenu and ImagelessMenu	

function MenuPageContents(P)
{
	/* The menu-level styles are applied to the page in the same way as for custom pages. */
	initPageStyles(P.menu);

	const stylePath = useStylePath();
	const styleDetails = useStyleDetails();

	const [currentLocation,setCurrentLocation] = createSignal();
	const [currentSpecial,setCurrentSpecial] = createSignal();

	/*
		Overlays are portalled out of the page, so neither the page wrapper's styling nor the
		styling of the section an item sits in can reach them through the DOM. The overlay wrapper
		applies pageStyling() directly, and the item hands its section styling over as it opens the
		overlay to layer on top.
	*/
	const [overlayStyling,setOverlayStyling] = createSignal({css:{}});

	const pageStyling = () => applicableStyles(stylePath,styleDetails());

	const overlayItem = () => subdoc(P.menu,currentLocation());
	const topSection = () => subdoc(P.menu,currentLocation().slice(0,2));

	const itemsExist = createMemo(() => P.menu.items?.find(i => i.status=='published'));

	const style = () => menuUsesImageCards(P.menu) ? 'grid' : 'imageless';

	const tit = () => P.menu?.title;

	return (<>
		<PageMeta title= {tit()}/>

		<div class='anchor' id='top' />

		<div>
{/* TODO compare this topContentStyle to hire page and home page styling. Remove it? */}
			<div class={css(theme().section)}> 
				<Show when={P.menu.teaser}>
					<div class={css(theme().text.teaser)}>{P.menu.teaser}</div>
				</Show>

				<Show when={P.menu.title && ! P.menu.hideTitle }>
					<h3 class={css(theme().text.pageTitle)}>{P.menu.title}</h3>
				</Show>

				<Show when={P.menu.tagline}>
					<div class={css(theme().text.tagline)}>{P.menu.tagline}</div>
				</Show>

				<Show when={P.menu.details}>
					<div class={css(theme().text.plain)} innerHTML={P.menu.details} />
				</Show>
			</div>

			<Show when={itemsExist()}>
				<div class={style()=='imageless' ? imagelessGridStyle() : standardGridStyle()}>
					<For each={P.menu.items}>{ (item,index) => 
						<Show when={item.status=='published'}>
							<MenuItem item={item} menu={P.menu} location={['items',index()]} styling={pageStyling()}
								setCurrentLocation={setCurrentLocation} setOverlayStyling={setOverlayStyling}
							/>
						</Show>
					}</For>
				</div>
			</Show>

			<Show when={P.menu.sections && P.menu.sections.length > 0}>
				<For each={P.menu.sections}>{ (section,index) => 
					<DisplaySectionOrWidget level={2}
						section={section}
						menu={P.menu}
						location={['sections',index()]}
						setCurrentLocation={setCurrentLocation}
						setOverlayStyling={setOverlayStyling}
						specials={P.specials}
						setCurrentSpecial={setCurrentSpecial}
					/>
				}</For>
			</Show>
		</div>

		{/* Overlays: */}

		<Show when={currentLocation()}>
			<Overlay iconOverImage={false} animation='slideIn' close={() => setCurrentLocation(undefined)}>
				<div class={css(pageStyling().css,overlayStyling().css)}>
					<OverlayItem
						menu={P.menu}
						location={currentLocation()}
						item={overlayItem()}
						section={topSection()}
					/>
				</div>
			</Overlay>
		</Show>

		{/* TODO add product type colouring/styling to special overlays? And maybe posters? */}
		{/* NOTE currentSpecial is a number and can be 0 */}
		<Show when={currentSpecial()!=null}>
			<Overlay iconOverImage={true} animation='slideIn' close={() => setCurrentSpecial(undefined)}>
				<ErrorBoundary fallback={(err) => <div>Problem showing item</div>}> 
					<div class={specialItemOverlayStyle()}>
						<SpecialsOverlayContent special={P.specials[currentSpecial()]} times={P.specials[currentSpecial()].times} />
					</div>
				</ErrorBoundary>  
			</Overlay>
		</Show>
	</>);
}

//TODO compare to MenuPageContents()
function Section(P)
{
	const stylePath = useStylePath();
	const styleDetails = useStyleDetails();

	const style = () => menuUsesImageCards(P.menu) ? 'grid' : 'imageless';

	const headingCss = () => menuPartCss(stylePath,styleDetails(),'menu-section-heading');

	return (<>
		<div class='anchor' id={menuSelector(P.menu,P.location)} />

		<section class={css(theme().section,P.styling.css)}>
			<Show when={P.section.teaser}>
				<div class={css(theme().text.teaser)}>{P.section.teaser}</div>
			</Show>

			<Show when={P.section.title}>
				<h3 class={cx(css(theme().title(P.level,P.productType,P.section),headingCss()),'title','menu-section-heading')}>{P.section.title}</h3>
			</Show>

			<Show when={P.section.tagline}>
				<div class={css(theme().text.tagline)}>{P.section.tagline}</div>
			</Show>

			<Show when={P.section.details}>
				<div class={detailsStyle()} innerHTML={P.section.details} />
			</Show>

			<div class={style()=='imageless' ? imagelessGridStyle() : standardGridStyle()}>
				<For each={P.section.items}>{ (item,index) => 
					<Show when={item.status=='published'}>
						<MenuItem {...P}
							location={[...P.location,'items',index()]} 
							setCurrentLocation={P.setCurrentLocation} 
							item={item} 
						/>
					</Show>
				}</For>
			</div>

			<Show when={P.section.sections && P.section.sections.length > 0}>
				<For each={P.section.sections}>{ (subsection,index) => 
					<DisplaySectionOrWidget {...P}
						level={P.level+1} 
						section={subsection} 
						location={[...P.location,'sections',index()]}
						setCurrentLocation={P.setCurrentLocation} 
						setCurrentSpecial={P.setCurrentSpecial} 
					/>
				}</For>
			</Show>
		</section>
	</>);
}

/*
	Sections and widgets carry their own style tags, so each of them adds an element to the style path
	and resolves the styles that apply to it - cf Component.tsx.
	The styles a section adds are also passed down to its subsections, items and widgets.
*/
function DisplaySectionOrWidget(P)
{
	const stylePath = useStylePath();
	const styleDetails = useStyleDetails();

	const productType = () => calculateProductType(P.menu,P.location);
	const isWidget = () => P.section.widget != undefined;

	/* 'menu-section' tracks the DOM <section> the old 'section {...}' menu css matched: a plain
	   section, and the specials widget, both render one; a button-row widget renders a <div>. */
	const rendersSection = () => !isWidget() || P.section.widget == 'specials';

	const newStylePath = () => [...stylePath,{
		key: isWidget() ? P.section.widget : 'section',
		childKey: P.location[P.location.length-1],
		classes: [productType(),...(P.section.classes ?? []),...(rendersSection() ? ['menu-section'] : [])]
	}];

	const newStyleDetails = createMemo(() => [
		...styleDetails(),
		...resolveStyleDetails(stylePath.map(e => e.key),P.section.styles)
	]);

	const applicStyles = createMemo(() => applicableStyles(newStylePath(),newStyleDetails()));

	return (
		<StylesProvider path={newStylePath()} styleDetails={newStyleDetails}>
			<IgnorePageMarginsStyle disable={! applicStyles().ignorePageMargins}>
				<AnimationWrap animations={applicStyles().animations}>
					<Switch>
						<Match when={isWidget()}>
							<Widget {...P} productType={productType()} widget={P.section} styling={applicStyles()} />
						</Match>
						<Match when={true}>
							<Section {...P} productType={productType()} styling={applicStyles()} />
						</Match>
					</Switch>
				</AnimationWrap>
			</IgnorePageMarginsStyle>
		</StylesProvider>
	);
}

function MenuItem(P) 
{
	const style = () => menuUsesImageCards(P.menu) ? 'grid' : 'imageless';

	/* TODO We could be more efficient about calculating this... */
	const productType = () => calculateProductType(P.menu,P.location);
	const id = createMemo(() => menuSelector(P.menu,P.location));

	return (<>
		<Switch>
			<Match when={style()=='grid'}>
				<a class={linkWrapStyle()} href=''
					onclick={e => {e.preventDefault(); P.setOverlayStyling(P.styling); P.setCurrentLocation(P.location)}}
				>
					<div id={id()} class='anchor' />
					<CardItem {...P} id={id()} productType={productType()} />
				</a>
			</Match>
			<Match when={style()=='imageless'}>
				<div id={id()} class='anchor' />
				<ImagelessItem {...P} id={id()} productType={productType()} />
			</Match>
		</Switch>
	</>);
}

/* Used by the backend */
export function MenuItemPreview(P) 
{
	const style = () => menuUsesImageCards(P.menu) ? 'grid' : 'imageless';
	/* TODO We could be more efficient about calculating this... */
	const productType = () => calculateProductType(P.menu,P.location);

	return (<>
		<Switch>
			<Match when={style()=='grid'}>
				<CardItem {...P} id='unused' productType={productType()} />
			</Match>
			<Match when={style()=='imageless'}>
				<ImagelessItem {...P} id='unused' productType={productType()} />
			</Match>
		</Switch>
	</>);
}

function Widget(P)
{
	return (
		<Switch>
			<Match when={P.widget.widget=='buttonRow'}>
				<ButtonsRowWidget {...P} />
			</Match>
			<Match when={P.widget.widget=='specials'}>
				<SpecialsWidget {...P} />
			</Match>
		</Switch>
	);
}

function ButtonsRowWidget(P)
{
	const navigate = useNavigate();

	const stylePath = useStylePath();
	const styleDetails = useStyleDetails();

	const jumpCss = () => menuPartCss(stylePath,styleDetails(),'menu-section-jump');

//XXX cf 'open in new window' open? ALSO make consistent between the sticky version and the non-sticky

	return (
		<Switch>
			<Match when={P.widget.sticky}>
				<div class={css(stickyStyle(),P.styling.css)}>
					<IgnorePageMarginsStyle>
						<SubNav>
							<For each={P.widget.buttons}>{button =>
								<button class={cx(subNavButtonStyle(),css(jumpCss()),button.style,'menu-section-jump')} onClick={() => jumpToAnchor(navigate,button.url)}>{button.label}</button>
							}</For>
						</SubNav>
					</IgnorePageMarginsStyle>
				</div>
			</Match>
			<Match when={true}>
				<div class={css(buttonRowStyle(),P.styling.css)}>
					<For each={P.widget.buttons}>{button =>
						<a href={button.url}>
							<button class={buttonRowButtonStyle()}>{button.label}</button>
						</a>
					}</For>
				</div>
			</Match>
		</Switch>
	);
}


function SpecialsWidget(P)
{
	const stylePath = useStylePath();
	const styleDetails = useStyleDetails();

	const headingCss = () => menuPartCss(stylePath,styleDetails(),'menu-section-heading');

//TODO insert title standard way + taglines etc

	return (
		<Show when={P.specials?.length > 0}>
			<div id='specials' />

			<section class={css(P.styling.css)}>
				<h3 class={cx(css(theme().title(P.level,P.productType,P.section),headingCss()),'title','menu-section-heading')}>Specials</h3>

				<div class={posterGridStyle()}>
					<For each={P.specials}>{(special,index) => 
						<a class={aStyle()} href='' onclick={() => P.setCurrentSpecial(index)} >
							<ErrorBoundary fallback={(err) => <div>Problem showing item</div>}>
								<Poster>
									<SpecialsPoster special={special} />
								</Poster>
							</ErrorBoundary>
						</a>
					}</For>
				</div>
			</section>
		</Show>
	);
}

//TODO share with backend??
function calculateProductType(menu,location:Location)
{
	if (menu.productType!=undefined && menu.productType!='mixed')
		return menu.productType;

	let data = menu;
	for (let i=0; i<location.length; i++) {
		data = data[location[i]];

		if (data.productType!=undefined && data.productType!='mixed')
			return data.productType;
	}

	return 'mixed';
}


/* Selectors used for sections, subsections and menu items. */
export function menuSelector(menu,location:Location)
{
	let subdoc = menu;
	let useSection = false;
	let useItems = false;
	let id = '';
	let sep = '';

	let i = 0;
	for (const e of location) {
		subdoc = subdoc[e];

		if (useSection) {
			id += sep + capitalise(subdoc.title ?? ''+i).replace(/\W/g,'');
			sep = '.';
		}
		if (useItems) {
			id += sep + capitalise(subdoc.name ?? ''+i).replace(/\W/g,'');
			sep = '.';
		}

		useSection = e=='sections';
		useItems = e=='items' ;
		i++;
	}
	return id;
}

